import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Badge, Button, Card, DataTable, Dialog, Field, Textarea } from '../../components/ui.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { toast } from '../../lib/toast.js';
import { dateNumeric, humanise, money, number } from '../../lib/format.js';

/**
 * What a person has put on the platform besides listings: the requirements
 * they posted as a tenant and the enquiries they sent as an owner, or the
 * enquiries an agency received. Shown on the user and broker pages so an
 * admin answering a complaint can see the record without asking the person.
 *
 * Contact details stay out, as they do in the admin lists these read from.
 */

/**
 * Deleting a requirement or an owner enquiry, with the reason the audit log
 * needs. Returns the button to put on a row and the dialog to render once.
 */
function useRemoval({ kind, remove, invalidate }) {
  const queryClient = useQueryClient();
  const [row, setRow] = useState(null);
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => remove(row._id, reason.trim()),
    onSuccess: () => {
      toast.success(kind === 'requirement' ? 'Requirement deleted' : 'Enquiry deleted');
      setRow(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: invalidate });
    },
    onError: (error) => toast.error(error?.message || 'That did not work. Try again.'),
  });
  const close = () => {
    setRow(null);
    setReason('');
  };

  const button = (target) =>
    target.deletedAt || target.ownerDeletedAt ? null : (
      <Button size="sm" variant="ghost" onClick={() => setRow(target)}>
        Delete
      </Button>
    );

  const dialog = (
    <Dialog
      open={Boolean(row)}
      onClose={close}
      title={kind === 'requirement' ? 'Delete this requirement?' : 'Delete this enquiry?'}
      warning={
        kind === 'requirement'
          ? 'It is closed and removed from the tenant’s requirements, and brokers stop being matched to it. Brokers who already unlocked it keep their lead. No credits are refunded to anyone. Recorded in the audit log against your account.'
          : 'It is removed from the owner’s list and closed for the agency if still open. The agency keeps what it unlocked. No credits are refunded. Recorded in the audit log against your account.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={reason.trim().length < 3}>
            Delete
          </Button>
        </>
      }
    >
      <Field label="Reason" hint="Recorded in the audit log against your account.">
        <Textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is it being deleted?" />
      </Field>
    </Dialog>
  );

  return { button, dialog };
}

/** "Deleted" in place of the status once the owner or an admin removed it. */
function deletedBadge(row) {
  return row.deletedAt || row.ownerDeletedAt ? <Badge tone="danger" label="Deleted" /> : null;
}

function subtitleFor(query, fallback) {
  const total = query.data?.meta?.total;
  return total ? `${number(total)} in total, ten most recent shown.` : fallback;
}

/** "Shankar Nagar, Avanti Vihar · Raipur" */
function where(location = {}) {
  const localities = (location.localities || []).slice(0, 3).join(', ');
  return [localities, location.city].filter(Boolean).join(' · ') || '—';
}

/** When the broker paid to unlock it, and how much. */
function unlockCell(row) {
  if (!row.unlockedAt) return <span className="table__muted">Not unlocked</span>;
  return (
    <div>
      <div className="nowrap">{dateNumeric(row.unlockedAt)}</div>
      {row.creditCost != null ? <div className="table__muted nowrap">{number(row.creditCost)} credits</div> : null}
    </div>
  );
}

/** "2–3 BHK", "2 BHK", or nothing. */
function bedrooms({ bedroomsMin: min, bedroomsMax: max }) {
  if (min == null && max == null) return null;
  if (min == null || max == null || min === max) return `${min ?? max} BHK`;
  return `${min}–${max} BHK`;
}

export function RequirementsCard({ userId }) {
  const { can } = useAuth();
  const removal = useRemoval({
    kind: 'requirement',
    remove: api.broker.removeRequirement,
    invalidate: ['admin-user-requirements', userId],
  });
  const query = useQuery({
    queryKey: ['admin-user-requirements', userId],
    queryFn: () => api.broker.requirements({ user: userId, pageSize: 10 }),
  });

  const columns = [
    { key: 'when', header: 'Posted', render: (row) => <span className="table__muted nowrap">{dateNumeric(row.createdAt)}</span> },
    {
      key: 'what',
      header: 'Looking for',
      render: (row) => (
        <div>
          <div className="truncate">{where(row.location)}</div>
          <div className="table__muted truncate">
            {[bedrooms(row), (row.propertyTypes || []).map(humanise).join(', ')]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      ),
    },
    {
      key: 'budget',
      header: 'Budget',
      className: 'table__numeric',
      render: (row) => <span className="nowrap">{money(row.budgetMinMinor)} – {money(row.budgetMaxMinor)}</span>,
    },
    { key: 'status', header: 'Status', render: (row) => deletedBadge(row) || <Badge status={row.status} /> },
    ...(can(PERMISSIONS.MODERATION_ACT) ? [{ key: 'actions', header: '', render: (row) => removal.button(row) }] : []),
  ];

  return (
    <>
    {removal.dialog}
    <Card title="Requirements posted" subtitle={subtitleFor(query, 'What they told brokers they are looking for.')} bodyless>
      <DataTable
        columns={columns}
        rows={query.data?.items || []}
        query={query}
        empty={{ title: 'No requirements', body: 'They have not posted what they are looking for.' }}
      />
    </Card>
    </>
  );
}

/**
 * Owner enquiries, from either end: `userId` for the ones an owner sent,
 * `brokerId` for the ones an agency received.
 */
export function EnquiriesCard({ userId, brokerId }) {
  const asOwner = Boolean(userId);
  const { can } = useAuth();
  const removal = useRemoval({
    kind: 'enquiry',
    remove: api.broker.removeEnquiry,
    invalidate: ['admin-enquiries', userId || null, brokerId || null],
  });
  const query = useQuery({
    queryKey: ['admin-enquiries', userId || null, brokerId || null],
    queryFn: () => api.broker.enquiries({ ...(asOwner ? { user: userId } : { broker: brokerId }), pageSize: 10 }),
  });

  const columns = [
    { key: 'when', header: 'Sent', render: (row) => <span className="table__muted nowrap">{dateNumeric(row.createdAt)}</span> },
    {
      key: 'property',
      header: 'Property',
      render: (row) => (
        <div>
          <div className="truncate">{row.property?.title || '—'}</div>
          <div className="table__muted truncate">
            {[row.property?.address?.locality, row.property?.address?.city].filter(Boolean).join(', ')}
            {row.property?.expectedRentMinor ? ` · ${money(row.property.expectedRentMinor)}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'unlock',
      header: 'Unlocked',
      render: (row) => unlockCell(row),
    },
    asOwner
      ? {
          key: 'broker',
          header: 'Agency',
          render: (row) =>
            row.broker?._id ? (
              <Link className="table__link" to={`/brokers/${row.broker._id}`}>{row.broker.businessName}</Link>
            ) : '—',
        }
      : {
          key: 'owner',
          header: 'Owner',
          render: (row) =>
            row.owner?._id ? <Link className="table__link" to={`/users/${row.owner._id}`}>{row.owner.name || 'Owner'}</Link> : '—',
        },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div>
          {deletedBadge(row) || <Badge status={row.status} />}
          <div className="table__muted nowrap">
            {!row.unlockedAt
              ? 'Not unlocked'
              : row.ownerResponse === 'accepted'
                ? 'Owner accepted'
                : row.ownerResponse === 'declined'
                  ? 'Owner declined'
                  : 'Waiting for the owner'}
          </div>
        </div>
      ),
    },
    ...(can(PERMISSIONS.MODERATION_ACT) ? [{ key: 'actions', header: '', render: (row) => removal.button(row) }] : []),
  ];

  return (
    <>
    {removal.dialog}
    <Card
      title={asOwner ? 'Owner enquiries sent' : 'Owner enquiries received'}
      subtitle={subtitleFor(query, asOwner ? 'Properties they asked an agency to let.' : 'Owners who hired this agency to let a property.')}
      bodyless
    >
      <DataTable
        columns={columns}
        rows={query.data?.items || []}
        query={query}
        empty={
          asOwner
            ? { title: 'No enquiries', body: 'They have not hired a broker.' }
            : { title: 'No enquiries', body: 'No owner has hired this agency yet.' }
        }
      />
    </Card>
    </>
  );
}

/**
 * Tenant leads, from either end: `brokerId` for the leads an agency was
 * matched to, `userId` for the brokers matched to one tenant's requirements.
 * Contact details are never in this list.
 */
export function LeadsCard({ userId, brokerId }) {
  const asTenant = Boolean(userId);
  const query = useQuery({
    queryKey: ['admin-leads', userId || null, brokerId || null],
    queryFn: () => api.broker.leads({ ...(asTenant ? { user: userId } : { broker: brokerId }), pageSize: 10 }),
  });

  const columns = [
    { key: 'when', header: 'Matched', render: (row) => <span className="table__muted nowrap">{dateNumeric(row.createdAt)}</span> },
    asTenant
      ? {
          key: 'broker',
          header: 'Agency',
          render: (row) =>
            row.broker?._id ? <Link className="table__link" to={`/brokers/${row.broker._id}`}>{row.broker.businessName}</Link> : '—',
        }
      : {
          key: 'tenant',
          header: 'Tenant',
          render: (row) => (
            <div>
              {row.tenant?._id ? <Link className="table__link" to={`/users/${row.tenant._id}`}>{row.tenant.name || 'Tenant'}</Link> : '—'}
              <div className="table__muted truncate">{where(row.requirement?.location)}</div>
            </div>
          ),
        },
    { key: 'unlock', header: 'Unlocked', render: (row) => unlockCell(row) },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div>
          <Badge status={row.status} />
          <div className="table__muted nowrap">
            {!row.unlockedAt
              ? 'Not unlocked'
              : row.tenantResponse === 'accepted'
                ? 'Tenant accepted'
                : row.tenantResponse === 'declined'
                  ? 'Tenant declined'
                  : row.tenantResponse === 'withdrawn'
                    ? 'Tenant withdrew'
                    : 'Waiting for the tenant'}
          </div>
        </div>
      ),
    },
  ];

  return (
    <Card
      title={asTenant ? 'Brokers matched to their requirements' : 'Tenant leads'}
      subtitle={subtitleFor(query, asTenant ? 'Agencies their requirements were offered to.' : 'Tenants this agency was matched to.')}
      bodyless
    >
      <DataTable
        columns={columns}
        rows={query.data?.items || []}
        query={query}
        empty={
          asTenant
            ? { title: 'No matches', body: 'No broker has been matched to their requirements.' }
            : { title: 'No leads', body: 'This agency has not been matched to a tenant yet.' }
        }
      />
    </Card>
  );
}

/** Deals a broker is working with a tenant, from the agency or the tenant side. */
export function DealsCard({ userId, brokerId }) {
  const asTenant = Boolean(userId);
  const query = useQuery({
    queryKey: ['admin-deals', userId || null, brokerId || null],
    queryFn: () => api.broker.deals({ ...(asTenant ? { user: userId } : { broker: brokerId }), pageSize: 10 }),
  });

  const columns = [
    {
      key: 'ref',
      header: 'Deal',
      render: (row) => (
        <div>
          <div className="nowrap">{row.reference || '—'}</div>
          <div className="table__muted nowrap">{dateNumeric(row.createdAt)}</div>
        </div>
      ),
    },
    {
      key: 'who',
      header: asTenant ? 'Agency' : 'Tenant',
      render: (row) =>
        asTenant
          ? row.broker?._id
            ? <Link className="table__link" to={`/brokers/${row.broker._id}`}>{row.broker.businessName}</Link>
            : '—'
          : row.tenant?._id
            ? <Link className="table__link" to={`/users/${row.tenant._id}`}>{row.tenant.name || 'Tenant'}</Link>
            : '—',
    },
    {
      key: 'property',
      header: 'Property',
      render: (row) => (
        <div>
          <div className="truncate">{row.property?.title || '—'}</div>
          <div className="table__muted nowrap">{money(row.rentMinor)} rent</div>
        </div>
      ),
    },
    {
      key: 'brokerage',
      header: 'Brokerage',
      className: 'table__numeric',
      render: (row) => (
        <div>
          <div className="nowrap">{money(row.brokerage?.totalMinor || 0)}</div>
          <div className="table__muted nowrap">{money(row.brokerage?.collectedMinor || 0)} collected</div>
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (row) => <Badge status={row.status} /> },
  ];

  return (
    <Card
      title="Deals"
      subtitle={subtitleFor(query, asTenant ? 'Rentals a broker is closing with them.' : 'Rentals this agency is closing with tenants.')}
      bodyless
    >
      <DataTable
        columns={columns}
        rows={query.data?.items || []}
        query={query}
        empty={{ title: 'No deals', body: asTenant ? 'No broker has opened a deal with them.' : 'This agency has not opened a deal yet.' }}
      />
    </Card>
  );
}
